import React, { useState, useEffect } from 'react';
import { X, Building2, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import PlaidLink from './PlaidLink';
import toast from 'react-hot-toast';

interface BankInformationProps {
  isOpen: boolean;
  onClose: () => void;
}

interface BankAccount {
  bankName: string;
  accountNumber: string;
  balance: string;
}

interface Expense {
  category: string;
  amount: number;
}

interface PlaidTransaction {
  amount: number;
  category: string[];
}

interface PlaidAccount {
  institution_name: string;
  mask: string;
  balances: {
    current: number;
  };
}

const LOCAL_STORAGE_KEY = 'bankData';

const BankInformation: React.FC<BankInformationProps> = ({ isOpen, onClose }) => {
  const [bankAccount, setBankAccount] = useState<BankAccount>({ bankName: '', accountNumber: '', balance: '' });
  const [income, setIncome] = useState<number>(0);
  const [expenses, setExpenses] = useState<Expense[]>([]);

  // Load saved data on mount
  useEffect(() => {
    const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedData) {
      const { bankAccount, income, expenses } = JSON.parse(savedData);
      setBankAccount(bankAccount || { bankName: '', accountNumber: '', balance: '' });
      setIncome(income || 0);
      setExpenses(expenses || []);
    }
  }, []);

  // Save data to local storage
  useEffect(() => {
    const bankData = { bankAccount, income, expenses };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(bankData));
  }, [bankAccount, income, expenses]);

  const handlePlaidSuccess = ({ transactions, accounts }: { transactions: PlaidTransaction[]; accounts: PlaidAccount[] }) => {
    try {
      if (!transactions || !accounts) {
        if (!navigator.onLine) {
          toast.error('You are offline. Please check your internet connection.');
          return;
        }
        throw new Error('Unable to fetch bank data');
      }

      // Calculate monthly income from deposits
      const deposits = transactions
        .filter(t => t.amount < 0) // Plaid uses negative for deposits
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      // Calculate expenses from withdrawals
      const expensesByCategory = transactions
        .filter(t => t.amount > 0)
        .reduce((acc, t) => {
          const category = t.category[0] || 'Other';
          acc[category] = (acc[category] || 0) + t.amount;
          return acc;
        }, {} as Record<string, number>);

      // Get total balance from all accounts
      const totalBalance = accounts
        .reduce((sum, account) => sum + account.balances.current, 0);

      // Update state with Plaid data
      setIncome(deposits);
      setExpenses(Object.entries(expensesByCategory).map(([category, amount]) => ({
        category,
        amount
      })));
      setBankAccount({
        bankName: accounts[0]?.institution_name || '',
        accountNumber: accounts[0]?.mask || '',
        balance: totalBalance.toString()
      });

      toast.success('Bank account connected successfully!');
      
      // Store Plaid data in localStorage for offline access
      localStorage.setItem('plaidData', JSON.stringify({
        transactions,
        accounts,
        lastUpdated: new Date().toISOString()
      }));
      
    } catch (error) {
      console.error('Error processing Plaid data:', error);
      toast.error('Error connecting bank account. Please try again.');
    }
  };
  
  // Load cached Plaid data when offline
  useEffect(() => {
    if (!navigator.onLine) {
      const cachedData = localStorage.getItem('plaidData');
      if (cachedData) {
        const { transactions, accounts, lastUpdated } = JSON.parse(cachedData);
        handlePlaidSuccess({ transactions, accounts });
        toast.info(`Using cached bank data from ${new Date(lastUpdated).toLocaleDateString()}`);
      }
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <header className="p-4 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800">Regular</h2>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6 md:h-8 md:w-8 text-gray-500" />
          </button>
        </header>

        <main className="overflow-y-auto flex-1 p-4 md:p-6 space-y-6">
          {/* Plaid Link */}
          <div className="mb-6 px-6 pt-6">
            <PlaidLink
              onSuccess={handlePlaidSuccess}
              onExit={() => toast.error('Bank connection cancelled')}
            />
            <p className="text-sm text-gray-500 mt-2 text-center">
              Securely connect your bank account to get personalized financial insights
            </p>
          </div>

          {/* Bank Account Section */}
          <section className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-5 w-5 text-blue-600" />
              <h3 className="font-medium text-blue-900">Regular Details</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Bank Name</label>
                <input
                  type="text"
                  placeholder="Enter bank name"
                  value={bankAccount.bankName}
                  onChange={(e) => setBankAccount({ ...bankAccount, bankName: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Account Number</label>
                <input
                  type="text"
                  placeholder="Enter account number"
                  value={bankAccount.accountNumber}
                  onChange={(e) => setBankAccount({ ...bankAccount, accountNumber: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Current Balance</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="0.00"
                    value={bankAccount.balance}
                    onChange={(e) => setBankAccount({ ...bankAccount, balance: e.target.value })}
                    className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <h3 className="font-medium text-green-900">Regular Income</h3>
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={income}
                  onChange={(e) => setIncome(parseFloat(e.target.value) || 0)}
                  className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                />
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="h-5 w-5 text-red-600" />
                <h3 className="font-medium text-red-900">Finance</h3>
              </div>
              <p className="text-xl md:text-2xl font-bold text-red-600">
                ${expenses.reduce((sum, expense) => sum + expense.amount, 0).toFixed(2)}
              </p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="h-5 w-5 text-purple-600" />
                <h3 className="font-medium text-purple-900">Health</h3>
              </div>
              <p className="text-xl md:text-2xl font-bold text-purple-600">
                ${(parseFloat(bankAccount.balance) || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </main>

        <footer className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </footer>
      </div>
    </div>
  );
};

export default BankInformation